import type { JournalAccount } from "@/apis/gql/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ButtonProps } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { BankAccountJournalAccountForm } from "../form/bank-account-journal-account-form";
import { ComboBox, type ComboOption } from "../tables/tableComboBox";

export type Props = {
  currentOption?: ComboOption;
  onChange: (value: ComboOption["value"]) => void;
  onFocus?: () => void;
  items: JournalAccount[];
  variant?: ButtonProps["variant"];
  allowUnknownAccount?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  className?: ButtonProps["className"];
  disabled?: boolean;
  fullWidth?: boolean;
  side?: "top" | "bottom";
  sideOffset?: number;
  container?: React.RefObject<HTMLElement>;
  readOnly?: boolean;
  modal?: boolean;
  entityId?: string;
  onCreate?: (journalAccountId: string) => Promise<void>;
};

export const JournalBankAccountsDropdown = ({
  currentOption,
  onChange,
  onFocus,
  onCreate,
  items,
  variant,
  placeholder = "Konto",
  className,
  disabled,
  fullWidth,
  sideOffset,
  container,
  side,
  readOnly,
  modal = false,
  entityId,
}: Props) => {
  const { t } = useTranslation();

  const [newAccountDialogOpen, setNewAccountDialogOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const handleCreateNew = (searchTerm: string) => {
    setSearchValue(searchTerm);
    setNewAccountDialogOpen(true);
  };

  const accountOptions = items.map(
    (account): ComboOption => ({
      label: account.name ?? "",
      alternativeLabels: [account.number ?? ""],
      value: account.journalAccountId,
      subLabel: account.number ?? "",
    }),
  );

  return (
    <>
      <ComboBox
        readOnly={readOnly}
        disabled={disabled}
        currentOption={currentOption}
        onChange={onChange}
        items={accountOptions ?? []}
        placeholder={placeholder}
        createNew={{
          label: t("accounts.new_title"),
          onCreateNew: handleCreateNew,
        }}
        emptyLabel={t("accounts.no_accounts_found")}
        variant={variant}
        className={className}
        popoverAlignStart
        fullWidth={fullWidth}
        onFocus={onFocus}
        sideOffset={sideOffset}
        side={side}
        container={container}
        modal={modal}
      />
      <Dialog
        open={newAccountDialogOpen}
        onOpenChange={setNewAccountDialogOpen}
      >
        <DialogContent
          className="sm:max-w-[425px] z-index-[100]"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{t("accounts.new_title")}</DialogTitle>
          </DialogHeader>
          <BankAccountJournalAccountForm
            account={{
              number: /^-?\d+$/.test(searchValue) ? searchValue : "",
              name: /^-?\d+$/.test(searchValue) === false ? searchValue : "",
              entityId,
            }}
            onSuccess={async (journalAccounts) => {
              setNewAccountDialogOpen(false);
              setSearchValue("");

              if (Array.isArray(journalAccounts)) {
                await Promise.all(
                  journalAccounts.map(async ({ journalAccountId }) => {
                    await onCreate?.(journalAccountId);

                    onChange(journalAccountId);
                  }),
                );
              } else {
                onChange(journalAccounts.journalAccountId);
                await onCreate?.(journalAccounts.journalAccountId);
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
